import { z } from "zod";

import { setWithExpiry, getWithExpiry } from "app/utils/sessionStorage";

import type { Headers } from "react-relay-network-modern/lib";

export const SamlProviderSchema = z.object({
  name: z.string(),
  idp_id: z.string(),
  display_name: z.string().optional(),
});

export const SamlProvidersArraySchema = z.array(SamlProviderSchema);

export type SamlProvider = z.infer<typeof SamlProviderSchema>;

interface Configs {
  cognito: {
    cognito_install: string;
    cognito_user_pool_region: string;
    cognito_user_pool_id: string;
    cognito_user_pool_client_id: string;
    saml: Record<string, string>;
    saml_providers?: SamlProvider[];
  };

  cubejs: {
    cubejs_domain: string;
  };

  deployment: {
    gql_uri: string;
    headers?: Headers;
    // Local-dev only (`just local-console`): absolute URL of the local IdP's
    // token endpoint, which the console hits directly to mint a JWT.
    local_idp_token_url?: string;
  };

  docs?: {
    docs_url: string;
  };

  jun0?: {
    jun0_url: string;
  };

  redash: {
    redash_uri: string;
  };

  sinistral: {
    sinistral_url: string;
  };

  superset?: {
    superset_uri: string;
  };
}

async function _fetchJun0Config(): Promise<Configs["jun0"] | undefined> {
  try {
    return await (await fetch("/config/jun0.json")).json();
  } catch {
    // Jun0 may not have config yet, which is ok.
    return undefined;
  }
}

async function _fetchSupersetConfig(): Promise<
  Configs["superset"] | undefined
> {
  try {
    return await (await fetch("/config/superset.json")).json();
  } catch {
    // Superset may not be deployed in this environment, which is ok.
    return undefined;
  }
}

async function _fetchConfigs(): Promise<Configs> {
  return {
    cognito: await (await fetch("/config/cognito.json")).json(),
    cubejs: await (await fetch("/config/cubejs.json")).json(),
    deployment: await (await fetch("/config/deployment.json")).json(),
    docs: await (await fetch("/config/docs.json")).json(),
    jun0: await _fetchJun0Config(),
    redash: await (await fetch("/config/redash.json")).json(),
    sinistral: await (await fetch("/config/sinistral.json")).json(),
    superset: await _fetchSupersetConfig(),
  };
}

export async function getConfigs(): Promise<Configs> {
  let configs: Configs;
  const configsJSON = getWithExpiry("configs");
  if (configsJSON) {
    configs = JSON.parse(configsJSON);
  } else {
    configs = await _fetchConfigs();
    setWithExpiry("configs", JSON.stringify(configs), 60);
  }
  return configs;
}
